/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Link } from '@coursera/cds-core';

import { buildCourseUrlRelative } from 'bundles/common/utils/urlUtils';
import useXdpSubfolderPrefixPath from 'bundles/internationalization-lib/hooks/useXdpSubfolderPrefixPath';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import CourseS12nInfo from 'bundles/video-logged-out-page/components/course-content/CourseS12nInfo';
import CourseTotalRatings from 'bundles/video-logged-out-page/components/course-content/CourseTotalRatings';
import TryCourseButton from 'bundles/video-logged-out-page/components/course-content/TryCourseButton';
import type { Partner, Rating, S12nInfo } from 'bundles/video-logged-out-page/types/vlpSharedTypes';

import _t from 'i18n!nls/video-logged-out-page';

const styles = {
  wrapperStyles: css`
    display: flex;
    padding: 20px 0 40px 0;
    border-top: none;
    margin: auto;
    width: 100%;
    max-width: 780px;
    justify-content: space-between;

    @media (max-width: 767px) {
      display: block;
      padding-bottom: 20px;
      padding-top: 1rem;
    }
  `,
  partnerLogoContainer: css`
    display: inline-block;
    height: 80px;
    width: 80px;
    padding-right: 4px;
    margin-right: 8px;

    @media (max-width: 767px) {
      height: 70px;
      width: 70px;
    }
  `,

  creatorLogo: css`
    @media (max-width: 767px) {
      height: 70px;
      width: 70px;
    }
  `,

  courseNameContainer: css`
    flex: 1;

    @media (max-width: 767px) {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  `,

  courseNameHeaders: css`
    line-height: normal;
    font-weight: bold;
    text-decoration: underline;
    margin-bottom: 0;
  `,

  courseInfoToggle: css`
    display: none;
    padding: 0;
    margin: 0 0 0 10px;

    @media (max-width: 767px) {
      display: inline-block;
    }
  `,

  partnerDesc: css`
    padding-bottom: 2px;
  `,
  partnerLink: css`
    color: black !important;
  `,
  courseInfoSectionContainer: css`
    padding-right: 20px;
    display: flex;
    justify-content: center;

    @media (max-width: 767px) {
      padding-right: 0;
    }
  `,
  infoContent: css`
    min-height: 80px;
    display: inline-block;
  `,
  courseInfoButtons: css`
    text-align: center;

    @media (max-width: 767px) {
      width: 100%;
      margin-top: 15px;
    }
  `,
};

type Props = {
  courseId: string | undefined | null;
  courseName: string | undefined | null;
  courseSlug: string;
  ratings?: Rating | null;
  totalReviewCount: number | undefined | null;
  totalEnrollmentCount: number | undefined | null;
  partners?: Array<Partner> | null;
  toggleInfo?: () => void;
  primaryS12n: S12nInfo | undefined | null;
};

type CourseNameHeaderProps = {
  cdpLink: string;
  courseName?: string | null;
  toggleInfo?: () => void;
};

type LogoContainerProps = {
  partner: Partner | null;
  logoSrc: string | null;
  cdpLink: string;
};

type PartnerNameProps = {
  partnerName: string;
  cdpLink: string;
};

const LOGO_HEIGHT = 80;
const LOGO_WIDTH = 80;

const LogoContainer: React.FC<LogoContainerProps> = ({ partner, logoSrc, cdpLink }) => {
  if (!partner || !cdpLink || !logoSrc) {
    return null;
  }

  return (
    <div css={styles.partnerLogoContainer}>
      <Link
        trackingName="vlp_university_logo"
        href={cdpLink}
        rel="noopener noreferrer"
        target="_blank"
        data-e2e="partner-logo-link"
        component={TrackedA}
        variant="quiet"
      >
        <Imgix
          src={logoSrc}
          key={partner.id}
          alt={partner.name}
          maxHeight={LOGO_HEIGHT}
          maxWidth={LOGO_WIDTH}
          height={LOGO_HEIGHT}
          width={LOGO_WIDTH}
          css={styles.creatorLogo}
        />
      </Link>
    </div>
  );
};

const CourseNameHeader: React.FC<CourseNameHeaderProps> = ({ cdpLink, courseName, toggleInfo }) => {
  if (!courseName || !cdpLink) {
    return null;
  }

  return (
    <div className="flex-1" data-e2e="course-name" css={styles.courseNameContainer}>
      <div css={styles.courseNameHeaders}>
        <Link
          href={cdpLink}
          trackingName="vlp_course_link"
          target="_blank"
          rel="noopener noreferrer"
          data-e2e="course-title-link"
          component={TrackedA}
          variant="standard"
          css={styles.partnerLink}
          typographyVariant="subtitleLarge"
        >
          {courseName}
        </Link>
      </div>
      {toggleInfo && (
        <i
          className="cif-chevron-up"
          css={styles.courseInfoToggle}
          tabIndex={0}
          onClick={toggleInfo}
          onKeyPress={toggleInfo}
          role="button"
        />
      )}
    </div>
  );
};

const PartnerName = ({ cdpLink, partnerName }: PartnerNameProps) => {
  if (!partnerName || !cdpLink) {
    return null;
  }

  return (
    <span className="flex-2" css={styles.partnerDesc}>
      <Link
        href={cdpLink}
        trackingName="vlp_university_name"
        target="_blank"
        rel="noopener noreferrer"
        data-e2e="partner-name-link"
        component={TrackedA}
        variant="quiet"
        css={styles.partnerLink}
        typographyVariant="bodySecondary"
      >
        {partnerName}
      </Link>
    </span>
  );
};

const CourseVideoOverview = ({
  ratings,
  partners,
  courseSlug,
  courseId,
  courseName,
  toggleInfo,
  totalEnrollmentCount,
  primaryS12n,
  totalReviewCount,
}: Props) => {
  const xdpSubfolderPrefixPath = useXdpSubfolderPrefixPath();

  const partner = partners && partners[0];
  const courseEnrollmentCount = totalEnrollmentCount;
  if (!partner) return null;
  const { id: partnerId = '', name: partnerName = '', squareLogo: partnerSquareLogo = '' } = partner;
  const logoSrc = partnerSquareLogo;
  const cdpLink = courseSlug && buildCourseUrlRelative(courseSlug, '', xdpSubfolderPrefixPath);
  const courseLink = cdpLink;
  const tryCourseButtonMessage = <FormattedMessage message={_t('Enroll for Free')} />;

  return (
    <div css={styles.wrapperStyles}>
      <div className="horizontal-box" css={styles.courseInfoSectionContainer}>
        <LogoContainer partner={partner} logoSrc={logoSrc} cdpLink={courseLink} />

        <div className="vertical-box" css={styles.infoContent}>
          <CourseNameHeader cdpLink={courseLink} courseName={courseName} toggleInfo={toggleInfo} />

          <PartnerName cdpLink={courseLink} partnerName={partnerName} />

          {ratings && (
            <CourseTotalRatings
              productLink={courseLink}
              courseEnrollmentCount={courseEnrollmentCount}
              ratings={ratings}
              totalReviewCount={Number(totalReviewCount)}
              a11yIdentifier="VideoOverview"
            />
          )}

          {primaryS12n && (
            <CourseS12nInfo s12n={primaryS12n} courseId={courseId} subfolderLocaleCode={xdpSubfolderPrefixPath} />
          )}
        </div>
      </div>
      {courseSlug && (
        <div css={styles.courseInfoButtons}>
          <TryCourseButton
            productLink={courseLink}
            buttonClass="try-course"
            customMessage={tryCourseButtonMessage}
            buttonName="enroll"
            sectionName="cta_banner"
          />
        </div>
      )}
    </div>
  );
};

export default CourseVideoOverview;
